import { yupResolver } from '@hookform/resolvers/yup'
import ErrorIcon from '@mui/icons-material/Error'
import { Alert, AppBar, Card, CardActions, CardContent, Checkbox, Grid, Stack, Tabs, Typography } from '@mui/material'
import { postCreateLoan } from '@src/api/loan/loan-api'
import SubmitButton from '@src/components/Common/Button/SubmitButton'
import CustomTab from '@src/components/Common/Tabs/CustomTab'
import TabPanel from '@src/components/Common/Tabs/TabPanel'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import BorrowerInputRow from '@src/components/LoanForm/BorrowerInputRow'
import FeeConfigurationInputRow from '@src/components/LoanForm/FeeConfigurationInputRow'
import FundedProductList from '@src/components/LoanForm/FundedProductList'
import InterestRateConfigurationList from '@src/components/LoanForm/InterestRateConfigurationList'
import {
  CustomerSchema,
  CustomerSchemaType,
  NewLoanDtoSchema,
  NewLoanDtoSchemaType,
} from '@src/components/LoanForm/loan-form-schema'
import LoanIdentificationInputRow from '@src/components/LoanForm/LoanIdentificationInputRow'
import PayoutList from '@src/components/LoanForm/PayoutList'
import PpaConfigInputRow from '@src/components/LoanForm/PpaConfigInputRow'
import RepaymentConfigInputRow from '@src/components/LoanForm/RepaymentConfigInputRow'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ChangeEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const LoanCreatePage = () => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(NewLoanDtoSchema),
    defaultValues: NewLoanDtoSchema.getDefault() as unknown as NewLoanDtoSchemaType,
  })

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [tabIndex, setTabIndex] = useState<number>(0)
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const [hasCoCustomer, setHasCoCustomer] = useState<boolean>(false)
  const handleCoApplicantChecked = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setValue('coBorrower', CustomerSchema.getDefault() as unknown as CustomerSchemaType, { shouldDirty: true })
    } else {
      setValue('coBorrower', null, { shouldDirty: true })
    }
    setHasCoCustomer(event.target.checked)
  }

  const onSubmit = (_data: NewLoanDtoSchemaType) => {
    postCreateLoan(_data)
      .then(() => {
        navigate('/loans')
      })
      .catch(reportErrorToConsole)
  }

  const fundedProductsInError = errors.loanData?.fundedProducts !== undefined
  const ratesOrPayoutInError = errors.loanData?.interestConfigs !== undefined || errors.loanData?.payouts != undefined
  const borrowerInError = errors.borrower !== undefined
  const coborrowerInError = errors.coBorrower !== undefined

  const configsInError =
    errors.loanData?.interestConfigs !== undefined ||
    errors.loanData?.financeFeeConfig !== undefined ||
    errors.loanData?.ppaConfig !== undefined

  return (
    <>
      <PageHeader
        title={t('loan.create.title')}
        breadcrumbs={{
          current: t('loan.create.title'),
          path: [{ href: '/loans', label: t('loan.title') }],
        }}
      />
      <PageContainer>
        <AppBar position="static">
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            aria-label="full width tabs example"
          >
            <CustomTab error={borrowerInError} label={t(`loan.borrower.borrower`)} />
            <CustomTab error={coborrowerInError} label={t(`loan.borrower.coBorrower`)} />
            <CustomTab error={errors.borrower !== undefined} label={t(`menus.loans.loans`)} />
            <CustomTab error={configsInError} label="Config" />
            <CustomTab error={fundedProductsInError} label={t('loan.fundedProducts')} />
            <CustomTab error={ratesOrPayoutInError} label={t('loan.rateAndPayment')} />
          </Tabs>
        </AppBar>
        <form id={'loan-form'} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardContent>
              {errors.loanData?.message && (
                <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
                  {errors.loanData?.message}
                </Alert>
              )}
              <Grid container spacing={2}>
                <TabPanel value={tabIndex} index={0}>
                  <BorrowerInputRow control={control} isBorrower={true} setValue={setValue} />
                </TabPanel>

                <TabPanel value={tabIndex} index={1}>
                  <Grid size={12}>
                    <Stack direction="row" rowGap={2} columnGap={1}>
                      <Typography>{t('loan.borrower.addCoBorrower')}</Typography>
                      <Checkbox checked={hasCoCustomer} onChange={handleCoApplicantChecked} />
                    </Stack>
                  </Grid>
                  {hasCoCustomer && <BorrowerInputRow control={control} isBorrower={false} setValue={setValue} />}
                </TabPanel>

                <Grid size={12}>
                  <TabPanel value={tabIndex} index={2}>
                    <LoanIdentificationInputRow control={control} setValue={setValue} />
                  </TabPanel>
                </Grid>

                <Grid size={12}>
                  <TabPanel value={tabIndex} index={3}>
                    <RepaymentConfigInputRow control={control} />
                    <FeeConfigurationInputRow control={control} />
                    <PpaConfigInputRow control={control} />
                  </TabPanel>
                </Grid>

                <Grid size={12}>
                  <TabPanel value={tabIndex} index={4}>
                    <FundedProductList
                      control={control}
                      name="loanData.fundedProducts"
                      values={watch('loanData.fundedProducts')}
                    />
                  </TabPanel>
                </Grid>

                <Grid size={12}>
                  <TabPanel value={tabIndex} index={5}>
                    <InterestRateConfigurationList
                      control={control}
                      name="loanData.interestConfigs"
                      values={watch('loanData.interestConfigs')}
                    />
                    <PayoutList control={control} name="loanData.payouts" />
                  </TabPanel>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'end' }}>
              <SubmitButton form="loan-form" />
            </CardActions>
          </Card>
        </form>
      </PageContainer>
    </>
  )
}

export default LoanCreatePage
